import React from "react";
import { useSelector } from "react-redux";
import { useGetUserTimeTrialBasedBadgesQuery } from "../../../redux-toolkit/features/my-activity/myActivityApiSlice";
import BaseJourney from "./BaseJourney";

const TimeTrialsJourney = ({ currentProgress }) => {
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;

  const {
    data: timeTrialBadgesData,
    isLoading,
  } = useGetUserTimeTrialBasedBadgesQuery(userId);

  // Get badges from API data
  const earnedBadges = timeTrialBadgesData?.data?.earnedBadges || [];

  // Sort badges by valueThreshold to ensure correct order
  const sortedBadges = [...earnedBadges].sort(
    (a, b) => a.valueThreshold - b.valueThreshold
  );

  // Calculate progress based on earned badges
  const earnedBadgesCount = sortedBadges.filter(
    (badge) => badge.isEarned
  ).length;
  const totalBadges = sortedBadges.length;
  const maxProgress = totalBadges || 3; // Use total badges as max progress
  const calculatedProgress = totalBadges > 0 ? earnedBadgesCount : 0;


  // Create timeline markers from badge thresholds
  const timelineMarkers =
    sortedBadges.length > 0
      ? sortedBadges.map((badge) => badge.valueThreshold.toString())
      : ["1", "3", "5"]; // Fallback markers

  // Transform API data to match our badge structure
  const badgeData =
    sortedBadges.length > 0
      ? sortedBadges.map((badge) => ({
          id: badge._id || badge.valueThreshold,
          image: badge.badgeImage || "",
          title: (
            badge.badgeName || `${badge.valueThreshold} Time Trials`
          ).toUpperCase(),
          description: getBadgeDescription(
            badge.badgeName,
            badge.valueThreshold
          ),
          isAchieved: Boolean(badge.isEarned),
        }))
      : []; // Empty array if no API data

  // Helper function to generate badge descriptions
  function getBadgeDescription(badgeName, threshold) {
    // Use the badge name to create descriptions, fallback to threshold-based descriptions
    if (badgeName) {
      const nameMap = {
        "1 TimeTrial Completed": "Your first speed challenge conquered",
        "3 TimeTrial Completed": "Speed demon in training",
        "5 TimeTrial Completed": "Halfway to speed mastery",
        "7 TimeTrial Completed": "Almost a speed legend",
        "10 TimeTrial Completed": "Speed master of the realm",
      };
      return (
        nameMap[badgeName] ||
        `Completed ${badgeName.replace(" TimeTrial Completed", "")} time trials`
      );
    }

    // Fallback descriptions based on threshold
    switch (threshold) {
      case 1:
        return "Your first speed challenge";
      case 2:
        return "Building speed momentum";
      case 3:
        return "Speed demon in training";
      case 4:
        return "Becoming a speedster";
      case 5:
        return "Halfway to speed mastery";
      case 6:
        return "Advanced speed techniques";
      case 7:
        return "Almost a speed legend";
      case 8:
        return "Speed master in training";
      case 9:
        return "Near speed perfection";
      case 10:
        return "Speed master of the realm";
      default:
        return `Complete ${threshold} time trials to earn this badge`;
    }
  }

  // Show loading state if badges are still loading
  if (isLoading) {
    return (
      <div className="journey-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            color: "#fff",
            fontSize: "1rem",
          }}
        >
          Loading time trial badges...
        </div>
      </div>
    );
  }

  return (
    <BaseJourney
      currentProgress={calculatedProgress}
      maxProgress={maxProgress}
      badgeData={badgeData}
      timelineMarkers={timelineMarkers}
      progressColor="#FF5722"
      actualScore={currentProgress} // Pass the actual time trials count for comparison with valueThreshold
    />
  );
};

export default TimeTrialsJourney;
